import { Button, Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { customerPublishState } from "./education-track-helpers.ts"
import type { EditionView } from "./types/education.types.ts"

const { Text } = Typography

const HIGH_SEVERITY = "high"

interface CustomerPublishBarProps {
  edition: EditionView
  /** `canEdit && permissions.can_publish` — resolved by the caller, same
   *  pattern `EducationTrackView` already uses for Regenerate/Education Center. */
  canPublish: boolean
  /** `permissions.can_approve` alone — the backend's `EditionApproveView`
   *  gates on `IsLeadManager`, independent of `lead_edit`. */
  canApprove: boolean
  canRegenerate: boolean
  publishing: boolean
  approving: boolean
  regenerating: boolean
  onOpenSources: () => void
  onEditLeadDetails: () => void
  onRegenerate: () => void
  onPublish: () => void
  onApprove: () => void
}

/**
 * Screen 5's footer — Facts and sources [n], a checks line, Edit lead
 * details, Regenerate, primary Publish customer guide, and Approve for
 * publishing when a manager can. The status/preview row lives in
 * `CustomerPublishStatus`, mounted separately (Screen 5's own header,
 * beside the audio player) — this component owns only what sits below the
 * article. Mounted by `EducationTrackView` only for the customer track
 * (never the sales brief, which has no publish concept at all — see the
 * "sales brief never shows a publish button" test), additive to the generic
 * edition header/footer Task 4 already built for every track.
 */
export function CustomerPublishBar({
  edition,
  canPublish,
  canApprove,
  canRegenerate,
  publishing,
  approving,
  regenerating,
  onOpenSources,
  onEditLeadDetails,
  onRegenerate,
  onPublish,
  onApprove
}: CustomerPublishBarProps) {
  const { t } = useTranslation()
  // Derived from the same `customerPublishState` the header status strip
  // reads, rather than re-testing `edition.status`/`published_at` here —
  // one source of truth for what "review required" / "already published"
  // means on this edition.
  const publishState = customerPublishState(edition)
  const needsReview = publishState === "reviewRequired"
  const highFlags = edition.safety_flags.filter((flag) => flag.severity === HIGH_SEVERITY)

  const publishTooltip = needsReview
    ? t("projectEducation.customerPublishBar.publishDisabledReview")
    : !canPublish
      ? t("projectEducation.customerPublishBar.publishDisabledNoAccess")
      : undefined

  return (
    <div data-testid={"customer-publish-bar"} className={"mt-4"}>
      <Flex justify={"space-between"} align={"flex-start"} wrap gap={"small"}>
        <Flex vertical gap={2}>
          <Button
            type={"link"}
            className={"!h-auto !p-0 self-start"}
            onClick={onOpenSources}
            data-testid={"facts-and-sources"}
          >
            {t("projectEducation.customerPublishBar.factsAndSources", {
              count: edition.citations.length
            })}
          </Button>
          {highFlags.length === 0 ? (
            <Text type={"secondary"} className={"text-xs"}>
              {t("projectEducation.customerPublishBar.checksClean")}
            </Text>
          ) : (
            <ul className={"list-disc pl-5 text-xs"} data-testid={"customer-publish-flags"}>
              {highFlags.map((flag, index) => (
                <li key={`${flag.code}-${index}`}>{flag.detail ?? flag.code}</li>
              ))}
            </ul>
          )}
        </Flex>

        <Flex gap={"small"} wrap align={"center"}>
          <Button type={"link"} onClick={onEditLeadDetails}>
            {t("projectEducation.customerPublishBar.editLeadDetails")}
          </Button>
          <Button loading={regenerating} disabled={!canRegenerate} onClick={onRegenerate}>
            {t("projectEducation.customerPublishBar.regenerate")}
          </Button>
          {needsReview && canApprove && (
            <Button
              loading={approving}
              onClick={onApprove}
              data-testid={"approve-for-publishing"}
            >
              {t("projectEducation.customerPublishBar.approve")}
            </Button>
          )}
          {publishState !== "published" && (
            <Tooltip title={publishTooltip}>
              <span>
                <Button
                  type={"primary"}
                  loading={publishing}
                  disabled={needsReview || !canPublish}
                  onClick={onPublish}
                  data-testid={"publish-customer-guide"}
                >
                  {t("projectEducation.customerPublishBar.publish")}
                </Button>
              </span>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </div>
  )
}
